/* Frost — collection page behavior. Frost copy of
   assets/orizza-collection.js, kept separate so either brand can change
   independently.
   sections/frost-collection-grid.liquid:
     - Show / Hide filters (desktop sidebar, remembered for the session;
       ≤989px it's a bottom sheet opened from a fixed bottom bar)
     - Sort by (desktop: white dropdown menu; ≤989px: bottom sheet. Both
       drive the same hidden <select> that is part of the filter form)
     - filters, sort and In stock toggle re-render the section
       through the Section Rendering API and push the new URL
     - infinite scroll: the next page is fetched as you near the bottom
       and its cards are appended to the grid (no button)
     - dual-thumb price slider synced with the two price fields
   sections/frost-collection-strip.liquid:
     - clicking a collection tile / the banner swaps the grid + breadcrumb
       to that collection (falls back to a normal page load if the target
       collection isn't on this template)
   Everything is delegated from document, so re-rendered markup needs no
   re-binding. Loaded by both sections; runs once. */
(function () {
  if (window.fzCollection) return;
  window.fzCollection = true;

  var STORE_KEY = 'fz-col-filters-open';
  var mobile = window.matchMedia('(max-width: 989px)');
  var requestId = 0;
  var priceTimer = null;

  var observer = null;
  var loadingMore = false;
  var retries = 0;

  function getRoot() {
    return document.querySelector('[data-fz-col]');
  }

  function readStored() {
    try { return sessionStorage.getItem(STORE_KEY); } catch (e) { return null; }
  }
  function writeStored(open) {
    try { sessionStorage.setItem(STORE_KEY, open ? '1' : '0'); } catch (e) {}
  }

  /* ---------- show / hide filters + sort (desktop menu / mobile sheet) ---------- */
  function updateLock(root) {
    var any = root.classList.contains('is-filters-open') || root.classList.contains('is-sort-open');
    document.documentElement.classList.toggle('fz-col-lock', any && mobile.matches);
  }

  function setSort(root, open) {
    root.classList.toggle('is-sort-open', open);
    if (open && root.classList.contains('is-filters-open') && mobile.matches) setFilters(root, false, false);
    root.querySelectorAll('[data-fz-col-sort-toggle]').forEach(function (b) {
      b.setAttribute('aria-expanded', open ? 'true' : 'false');
    });
    updateLock(root);
  }

  function setFilters(root, open, remember) {
    root.classList.toggle('is-filters-open', open);
    if (open) setSort(root, false);
    var toggle = root.querySelector('[data-fz-col-filter-toggle]');
    if (toggle) {
      toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      var label = toggle.querySelector('[data-fz-col-filter-label]');
      if (label) label.textContent = open ? root.dataset.hideLabel : root.dataset.showLabel;
    }
    updateLock(root);
    if (remember && !mobile.matches) writeStored(open);
  }

  // the mobile sheet shows every facet expanded (no accordion)
  function prepFacets(root) {
    if (!mobile.matches) return;
    root.querySelectorAll('details.fz-col__facet').forEach(function (d) { d.open = true; });
  }

  function initRoot(root) {
    if (!root || root.dataset.fzColReady) return;
    root.dataset.fzColReady = '1';
    var stored = readStored();
    var open = stored === null ? root.dataset.defaultOpen === 'true' : stored === '1';
    setFilters(root, open && !mobile.matches, false);
    initPrice(root);
    prepFacets(root);
    initInfinite(root);
  }

  /* ---------- price ---------- */
  function toNumber(value) {
    var n = parseInt(String(value).replace(/[^\d]/g, ''), 10);
    return isNaN(n) ? 0 : n;
  }
  function format(n) {
    try { return n.toLocaleString('en-IN'); } catch (e) { return String(n); }
  }

  function syncPrice(box, from) {
    var max = toNumber(box.dataset.max);
    var rMin = box.querySelector('[data-fz-col-range="min"]');
    var rMax = box.querySelector('[data-fz-col-range="max"]');
    var iMin = box.querySelector('[data-fz-col-price-input="min"]');
    var iMax = box.querySelector('[data-fz-col-price-input="max"]');
    var lo, hi;
    if (from === 'input') {
      lo = toNumber(iMin.value);
      hi = iMax.value.trim() === '' ? max : toNumber(iMax.value);
    } else {
      lo = toNumber(rMin.value);
      hi = toNumber(rMax.value);
    }
    lo = Math.max(0, Math.min(lo, max));
    hi = Math.max(0, Math.min(hi, max));
    if (lo > hi) {
      if (from === 'max') hi = lo; else lo = hi;
    }
    rMin.value = lo;
    rMax.value = hi;
    iMin.value = format(lo);
    iMax.value = format(hi);
    var track = box.querySelector('.fz-col__range-track');
    if (track && max > 0) {
      track.style.setProperty('--fz-min', (lo / max) * 100 + '%');
      track.style.setProperty('--fz-max', (hi / max) * 100 + '%');
    }
  }

  function initPrice(root) {
    root.querySelectorAll('[data-fz-col-price]').forEach(function (box) { syncPrice(box, 'input'); });
  }

  /* ---------- params ---------- */
  function buildParams(root) {
    var form = root.querySelector('[data-fz-col-form]');
    var params = new URLSearchParams();
    if (form) {
      new FormData(form).forEach(function (value, key) {
        value = String(value).trim();
        if (value !== '') params.append(key, value);
      });
      form.querySelectorAll('[data-fz-col-price]').forEach(function (box) {
        var max = toNumber(box.dataset.max);
        var iMin = box.querySelector('[data-fz-col-price-input="min"]');
        var iMax = box.querySelector('[data-fz-col-price-input="max"]');
        var lo = toNumber(iMin.value);
        var hi = toNumber(iMax.value);
        if (lo > 0) params.set(iMin.dataset.name, lo);
        if (hi < max) params.set(iMax.dataset.name, hi);
      });
    } else {
      // no filters on this collection — still pick up sort / toggle
      root.querySelectorAll('select[name], input[name]').forEach(function (el) {
        if (el.type === 'checkbox' && !el.checked) return;
        if (el.value) params.append(el.name, el.value);
      });
    }
    return params;
  }

  /* ---------- render ---------- */
  function render(root, search, opts) {
    opts = opts || {};
    var id = ++requestId;
    var sectionId = root.dataset.sectionId;
    var path = window.location.pathname;
    var query = search ? '&' + search : '';
    root.classList.add('is-loading');

    fetch(path + '?section_id=' + encodeURIComponent(sectionId) + query)
      .then(function (res) {
        if (!res.ok) throw new Error(res.status);
        return res.text();
      })
      .then(function (html) {
        if (id !== requestId) return;
        var fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-fz-col]');
        if (!fresh) throw new Error('missing section');

        var openState = {};
        root.querySelectorAll('details[data-key]').forEach(function (d) { openState[d.dataset.key] = d.open; });

        root.querySelectorAll('[data-fz-col-render]').forEach(function (el) {
          var next = fresh.querySelector('[data-fz-col-render="' + el.dataset.fzColRender + '"]');
          if (next) el.innerHTML = next.innerHTML;
        });

        root.querySelectorAll('details[data-key]').forEach(function (d) {
          if (d.dataset.key in openState) d.open = openState[d.dataset.key];
        });
        initPrice(root);
        prepFacets(root);
        initInfinite(root);

        var url = path + (search ? '?' + search : '');
        if (opts.push !== false) history.pushState({ fzCol: true }, '', url);
        root.classList.remove('is-loading');

        if (opts.scroll) {
          var toolbar = root.querySelector('.fz-col__toolbar');
          if (toolbar) window.scrollTo({ top: toolbar.getBoundingClientRect().top + window.scrollY - 90, behavior: 'smooth' });
        }
      })
      .catch(function () {
        if (id !== requestId) return;
        window.location.href = path + (search ? '?' + search : '');
      });
  }

  function submit(root) {
    render(root, buildParams(root).toString());
  }

  /* ---------- infinite scroll ---------- */
  function initInfinite(root) {
    if (observer) { observer.disconnect(); observer = null; }
    loadingMore = false;
    retries = 0;
    if (!root || !('IntersectionObserver' in window)) return; // keeps normal pagination
    root.classList.add('is-infinite');
    var more = root.querySelector('[data-fz-col-more]');
    if (!more) return;
    observer = new IntersectionObserver(function (entries) {
      if (entries[0].isIntersecting) loadMore(root);
    }, { rootMargin: '0px 0px 800px 0px' });
    observer.observe(more);
  }

  function loadMore(root) {
    var more = root.querySelector('[data-fz-col-more]');
    if (!more || loadingMore) return;
    loadingMore = true;
    more.classList.add('is-loading');
    var id = requestId; // a filter/sort change bumps requestId and discards this result

    var next = new URL(more.dataset.nextUrl, window.location.origin);
    next.searchParams.set('section_id', root.dataset.sectionId);

    fetch(next.pathname + '?' + next.searchParams.toString())
      .then(function (res) {
        if (!res.ok) throw new Error(res.status);
        return res.text();
      })
      .then(function (html) {
        if (id !== requestId) return;
        var fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-fz-col]');
        var grid = root.querySelector('.fz-col__grid');
        var freshGrid = fresh && fresh.querySelector('.fz-col__grid');
        if (!grid || !freshGrid) throw new Error('missing grid');

        while (freshGrid.firstChild) grid.appendChild(freshGrid.firstChild);
        retries = 0;

        var freshMore = fresh.querySelector('[data-fz-col-more]');
        if (freshMore) {
          more.dataset.nextUrl = freshMore.dataset.nextUrl;
          more.classList.remove('is-loading');
          loadingMore = false;
          // re-observe so it fires again if the sentinel is still near the viewport
          if (observer) { observer.unobserve(more); observer.observe(more); }
        } else {
          if (observer) observer.disconnect();
          more.remove();
          loadingMore = false;
        }
      })
      .catch(function () {
        if (id !== requestId) return;
        loadingMore = false;
        more.classList.remove('is-loading');
        retries++;
        if (retries <= 3) {
          setTimeout(function () {
            if (id !== requestId || !observer) return;
            observer.unobserve(more);
            observer.observe(more); // re-triggers if still near the viewport
          }, 2000);
        }
      });
  }

  /* ---------- switch collection (top strip) ---------- */
  function sectionIdOf(el) {
    var wrap = el && el.closest('.shopify-section');
    return wrap ? wrap.id.replace(/^shopify-section-/, '') : null;
  }

  function markActive(pathname) {
    document.querySelectorAll('[data-fz-col-switch]').forEach(function (link) {
      var active = new URL(link.href, window.location.origin).pathname === pathname;
      link.classList.toggle('is-active', active);
      if (active) link.setAttribute('aria-current', 'page'); else link.removeAttribute('aria-current');
    });
  }

  function switchCollection(link) {
    var root = getRoot();
    var target = new URL(link.href, window.location.origin);
    var gridId = sectionIdOf(root);
    var crumbId = sectionIdOf(document.querySelector('.frost-breadcrumb-section'));
    var ids = [gridId];
    if (crumbId) ids.push(crumbId);
    var id = ++requestId;
    root.classList.add('is-loading');

    fetch(target.pathname + '?sections=' + ids.map(encodeURIComponent).join(','))
      .then(function (res) {
        if (!res.ok) throw new Error(res.status);
        return res.json();
      })
      .then(function (data) {
        if (id !== requestId) return;
        if (!data || !data[gridId]) throw new Error('template mismatch');
        ids.forEach(function (sid) {
          var current = document.getElementById('shopify-section-' + sid);
          if (!current || !data[sid]) return;
          var parsed = new DOMParser().parseFromString(data[sid], 'text/html');
          var incoming = parsed.getElementById('shopify-section-' + sid);
          current.innerHTML = incoming ? incoming.innerHTML : parsed.body.innerHTML;
        });
        var fresh = getRoot();
        initRoot(fresh); // also re-arms infinite scroll for the new collection
        if (fresh && fresh.dataset.pageTitle) document.title = fresh.dataset.pageTitle;
        history.pushState({ fzCol: true }, '', target.pathname + target.search);
        markActive(target.pathname);
      })
      .catch(function () {
        if (id !== requestId) return;
        window.location.href = link.href;
      });
  }

  /* ---------- events ---------- */
  document.addEventListener('click', function (event) {
    var root = getRoot();

    /* desktop: click outside closes the sort menu */
    if (root && !mobile.matches && root.classList.contains('is-sort-open') && !event.target.closest('.fz-col__sort')) {
      setSort(root, false);
    }

    var switchLink = event.target.closest('a[data-fz-col-switch]');
    if (switchLink) {
      if (!root || event.metaKey || event.ctrlKey || event.shiftKey || switchLink.getAttribute('href') === '#') return;
      var target = new URL(switchLink.href, window.location.origin);
      if (target.origin !== window.location.origin || target.pathname.indexOf('/collections/') === -1) return;
      event.preventDefault();
      if (target.pathname === window.location.pathname && !window.location.search) return;
      switchCollection(switchLink);
      return;
    }

    if (!root || !root.contains(event.target)) return;

    /* sort: desktop dropdown + mobile sheet */
    if (event.target.closest('[data-fz-col-sort-toggle]')) {
      setSort(root, !root.classList.contains('is-sort-open'));
      return;
    }
    var sortOpt = event.target.closest('[data-fz-sort-value]');
    if (sortOpt) {
      var select = root.querySelector('.fz-col__sort-select');
      if (select && select.value !== sortOpt.dataset.fzSortValue) {
        select.value = sortOpt.dataset.fzSortValue;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
      setSort(root, false);
      return;
    }

    /* "All" pill: clears every value of that facet */
    var allBtn = event.target.closest('[data-fz-col-clear-facet]');
    if (allBtn) {
      var facet = allBtn.closest('.fz-col__facet');
      if (facet) facet.querySelectorAll('input[type="checkbox"]').forEach(function (c) { c.checked = false; });
      submit(root);
      return;
    }

    if (event.target.closest('[data-fz-col-filter-toggle]')) {
      setFilters(root, !root.classList.contains('is-filters-open'), true);
      return;
    }
    if (event.target.closest('[data-fz-col-filter-close]')) {
      setFilters(root, false, false);
      setSort(root, false);
      return;
    }
    var pageLink = event.target.closest('a[data-fz-col-page]');
    if (pageLink && !event.metaKey && !event.ctrlKey) {
      event.preventDefault();
      var url = new URL(pageLink.href, window.location.origin);
      render(root, url.searchParams.toString(), { scroll: !!pageLink.closest('.fz-col__pagination') });
    }
  });

  document.addEventListener('change', function (event) {
    var root = getRoot();
    if (!root || !root.contains(event.target)) return;
    var el = event.target;

    /* mobile In stock switch (no name) -> forward to the real toolbar checkbox */
    if (el.matches('[data-fz-col-stock-proxy]')) {
      var real = root.querySelector('.fz-col__stock-input[name]');
      if (real) { real.checked = el.checked; submit(root); }
      return;
    }

    if (el.matches('[data-fz-col-range]')) return; // handled on input (debounced)
    if (el.matches('[data-fz-col-price-input]')) {
      syncPrice(el.closest('[data-fz-col-price]'), 'input');
    }
    if (el.name || el.matches('[data-fz-col-price-input]')) submit(root);
  });

  document.addEventListener('input', function (event) {
    var el = event.target;
    if (!el.matches || !el.matches('[data-fz-col-range]')) return;
    var root = getRoot();
    syncPrice(el.closest('[data-fz-col-price]'), el.dataset.fzColRange);
    clearTimeout(priceTimer);
    priceTimer = setTimeout(function () { submit(root); }, 600);
  });

  document.addEventListener('submit', function (event) {
    var root = getRoot();
    if (!root || !event.target.matches('[data-fz-col-form]')) return;
    event.preventDefault();
    submit(root);
  });

  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Escape') return;
    var root = getRoot();
    if (!root) return;
    setSort(root, false);
    if (mobile.matches) setFilters(root, false, false);
  });

  window.addEventListener('popstate', function () {
    var root = getRoot();
    if (!root) return;
    var base = root.dataset.collectionUrl || '';
    if (base && window.location.pathname.slice(-base.length) === base) {
      render(root, window.location.search.replace(/^\?/, ''), { push: false });
    } else {
      window.location.reload();
    }
  });

  mobile.addEventListener && mobile.addEventListener('change', function () {
    var root = getRoot();
    if (!root) return;
    setSort(root, false);
    var stored = readStored();
    setFilters(root, !mobile.matches && stored === '1', false);
    prepFacets(root);
  });

  document.addEventListener('shopify:section:load', function () { initRoot(getRoot()); });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initRoot(getRoot()); });
  } else {
    initRoot(getRoot());
  }
})();